import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Modal } from '@mui/material';
import queryString from 'query-string';
import Api from '../API';
import CloseIcon from '@mui/icons-material/Close';
import { IJob } from '../../Job/types';
import { createJobDefault, emojiRegex, resourceDefault } from '../config';
import { classes } from './styles';
import GenericDialog from '../../Components/Modals/GenericDialog';
import ConfirmationDialog from '../../Components/Modals/ConfirmationDialog';
import { IGeneratedContent } from '../../../AIStudio/NewJobIntegration/AIStudioNewJob';
import moment from 'moment';
import { IFilters, INewJobDefaults, INewJobParam } from '../types';
import { StyledSnackbarProps } from '../../Components/CustomUIElements/StyledSnackbar';
import NewJobSidebar from './NewJobSidebar';
import NewJobStep1 from './NewJobStep1';
import NewJobStep2 from './NewJobStep2';
import NewJobStep3 from './NewJobStep3';

export default function NewJob({
  jobDefaults,
  setFilters,
  filters,
  isDialogOpen,
  setIsDialogOpen,
  fromApproval,
  approvalFormId,
  aiStudioEnabled,
  showEntitySection,
  setSnackbar,
  userTimezone
}: {
  jobDefaults?: Partial<Record<keyof INewJobDefaults, INewJobParam['value']>>;
  setFilters?: (filter: IFilters) => void;
  filters?: IFilters;
  isDialogOpen: boolean;
  setIsDialogOpen: Dispatch<SetStateAction<boolean>>;
  fromApproval?: boolean;
  approvalFormId?: number;
  aiStudioEnabled: boolean;
  showEntitySection: boolean;
  setSnackbar: StyledSnackbarProps['setSnackbarState'];
  userTimezone: string;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [confirmCancelIsOpen, setConfirmCancelIsOpen] = useState(false);
  const [confirmCreatedJobOpen, setConfirmCreatedJobOpen] = useState(false);
  const [confirmFailJobOpen, setConfirmFailJobOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [createType, setCreateType] = useState<string>('Create new');
  const [resources, setResources] = useState<Record<string, boolean>>(resourceDefault);
  const [jobOptions, setJobOptions] = useState([]);
  const [jobDefaultOptions, setJobDefaultOptions] = useState([]);
  const [clonedJob, setClonedJob] = useState<string | null>(null);
  const [copiedJob, setCopiedJob] = useState<IJob | null>(null);
  const [error, setError] = useState(false);
  const [newJobData, setNewJobsData] = useState<typeof createJobDefault>(createJobDefault);
  const [editJobUrl, setEditJobUrl] = useState<string | null>(null);
  const [generatedContent, setGeneratedContent] = useState<IGeneratedContent>({
    talentLandingPage: '',
    screeningQuestions: []
  });
  const [tlpPreviewCollapsed, setTlpPreviewCollapsed] = useState(true);
  const [entities, setEntities] = useState<{ id: number; name: string }[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<{ id: number; name: string } | null>(null);
  const [loadingEntities, setLoadingEntities] = useState<boolean>(false);

  useEffect(() => {
    if (!jobDefaults) return;
    setNewJobsData((prev) => ({
      ...prev,
      ...Object.entries(jobDefaults).reduce((acc: any, [key, value]) => {
        acc[key] = {
          ...prev[key as keyof typeof prev],
          value
        };
        return acc;
      }, {})
    }));
  }, [jobDefaults]);

  const fieldRefs = {
    reference: useRef<HTMLInputElement>(null),
    title: useRef<HTMLInputElement>(null),
    location: useRef<HTMLInputElement>(null),
    expires_at: useRef<HTMLInputElement>(null),
    cloneJob: useRef<HTMLInputElement>(null)
  };

  const getEntities = async () => {
    setLoadingEntities(true);
    try {
      const { res } = await Api.getEntities();
      setEntities(res.entities);
      setSelectedEntity(res.current_entity);
    } catch (error) {
      setSnackbar({
        message: `There was an error fetching entities, ${error}`,
        state: 'error'
      });
    } finally {
      setLoadingEntities(false);
    }
  };

  const checkReference = async (isValid: boolean) => {
    setIsLoading(true);
    try {
      const response = await Api.referenceValidation({
        'job[reference]': newJobData.reference.value
      });
      if (response.res.valid) {
        isValid && setCurrentStep(currentStep + 1);
      } else {
        if (response.res.correct_format === false) {
          newJobData?.reference?.value &&
            setNewJobsData((prev) => ({
              ...prev,
              reference: {
                ...prev.reference,
                error: 'Incorrect reference format, prefix letters before digits'
              }
            }));
        } else {
          newJobData?.reference?.value &&
            setNewJobsData((prev) => ({
              ...prev,
              reference: { ...prev.reference, error: 'Reference already in use' }
            }));
        }
        fieldRefs['reference']?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch (error) {
      setSnackbar({
        message: `There was an error in checking the reference, ${error}`,
        state: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const cloneJob = async () => {
    setIsLoading(true);
    try {
      const data = await Api.importResources(
        copiedJob?.id,
        {
          job: {
            reference: newJobData.reference.value,
            title: newJobData.title.value,
            expires_at: newJobData.expires_at.value,
            location: newJobData.location.value,
            requisition_ids: fromApproval ? [approvalFormId] : undefined
          }
        },
        `?${queryString.stringify(
          { ...resources, 'resources[modal]': true },
          { arrayFormat: 'bracket', encode: false }
        )}`
      );
      setConfirmCreatedJobOpen(true);
      setEditJobUrl(`${window.location.origin}${data?.res?.job_link}/edit`);
    } catch (error) {
      setConfirmFailJobOpen(true);
      setSnackbar({
        message: `There was an error in cloning the job, ${error}`,
        state: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createJob = async () => {
    setIsLoading(true);
    const questions =
      generatedContent.screeningQuestions.length > 0
        ? generatedContent.screeningQuestions.map((question) => ({
            ...question,
            field_type: 'text_area_field'
          }))
        : null;
    try {
      const data = await Api.createJob({
        job: {
          ...jobDefaults,
          reference: newJobData.reference.value,
          title: newJobData.title.value,
          expires_at: newJobData.expires_at.value,
          location: newJobData.location.value,
          ...(generatedContent.talentLandingPage && {
            description: generatedContent.talentLandingPage
          }),
          entity_id: selectedEntity ? selectedEntity.id : undefined,
          requisition_ids: fromApproval ? [approvalFormId] : undefined
        },
        fields: questions
      });
      setConfirmCreatedJobOpen(true);
      setEditJobUrl(`${window.location.origin}${data?.res?.job_link}/edit`);
    } catch (error) {
      setConfirmFailJobOpen(true);
      setSnackbar({
        message: `There was an error in creating the job, ${error}`,
        state: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      const [clonedOptions, prefix] = await Promise.all([
        Api.cloneJobsOptions({}),
        Api.referencePrefix({})
      ]);
      setJobDefaultOptions(clonedOptions?.res?.jobs);
      setNewJobsData((prev) => ({
        ...prev,
        reference: {
          ...prev.reference,
          value: prefix?.res?.auto_job_reference_prefix || ''
        }
      }));
      setJobOptions(clonedOptions?.res?.jobs.map((a) => `${a.reference} - ${a.title}`));
    } catch (err) {
      setSnackbar({
        message: `There was an error in getting the jobs, ${err}`,
        state: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  async function getJob(jobId: number) {
    setIsLoading(true);
    try {
      const data = await Api.getJob(jobId);
      setCopiedJob(data?.res);
    } catch (err) {
      setSnackbar({
        message: `There was an error in getting the job data, ${err}`,
        state: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleValidateFields = () => {
    let isValid = true;
    let firstError: keyof typeof fieldRefs | undefined = undefined;

    // Validate applicant detial fields
    Object.entries(newJobData).forEach((field) => {
      let tempErrorMessage = '';
      if (field[1].required && !field[1].value) {
        if (!firstError) {
          firstError = field[0] as keyof typeof fieldRefs;
        }
        isValid = false;
        tempErrorMessage = 'This field is required';
      }
      if (field[0] === 'title' || field[0] === 'location') {
        if (field[1].value.match(emojiRegex)) {
          isValid = false;
          tempErrorMessage = "This field can't have emojis";
        }
      }
      if (field[0] === 'expires_at') {
        if (!moment(newJobData.expires_at.value).isValid()) {
          isValid = false;
          tempErrorMessage = 'This field must be a valid date';
        } else {
          if (!moment(newJobData.expires_at.value).isAfter(moment())) {
            isValid = false;
            tempErrorMessage = 'Date and Time must be today or in the future';
          }
        }
      }
      setNewJobsData((prev) => ({
        ...prev,
        [field[0]]: { ...prev[field[0] as keyof typeof prev], error: tempErrorMessage }
      }));
    });

    // Scroll to first invalid field
    if (firstError) {
      fieldRefs[firstError]?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    checkReference(isValid);
    return isValid;
  };

  const clearData = () => {
    setNewJobsData(createJobDefault);
    setIsDialogOpen(false);
    setError(false);
    setCurrentStep(1);
    setCreateType('Create new');
    setResources(resourceDefault);
    setClonedJob(null);
    setNewJobsData((prev) => ({
      ...prev,
      location: { ...prev.location, value: '' },
      reference: { ...prev.reference, value: '' },
      title: { ...prev.title, value: '' },
      expires_at: { ...prev.expires_at, value: '' }
    }));
    setGeneratedContent({
      talentLandingPage: '',
      screeningQuestions: []
    });
    setSelectedEntity(entities[0]);
  };

  useEffect(() => {
    getJobs();
    getEntities();
  }, [getJobs]);

  return (
    <Modal
      open={isDialogOpen}
      onClose={() => setConfirmCancelIsOpen(true)}
      aria-labelledby="new-job-modal"
    >
      <Box sx={classes.actionsModal}>
        <NewJobSidebar
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          fromApproval={fromApproval}
          setError={setError}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '75%',
            paddingLeft: '40px',
            paddingTop: '60px',
            paddingBottom: '40px'
          }}
        >
          <CloseIcon onClick={() => setConfirmCancelIsOpen(true)} sx={classes.closeIcon} />
          {currentStep === 1 && (
            <NewJobStep1
              createType={createType}
              setCreateType={setCreateType}
              clonedJob={clonedJob}
              setClonedJob={setClonedJob}
              jobOptions={jobOptions}
              jobDefaultOptions={jobDefaultOptions}
              getJob={getJob}
              error={error}
              resources={resources}
              setResources={setResources}
            />
          )}
          {currentStep === 2 && (
            <NewJobStep2
              newJobData={newJobData}
              setNewJobsData={setNewJobsData}
              fieldRefs={fieldRefs}
              showEntitySection={showEntitySection}
              entities={entities}
              loadingEntities={loadingEntities}
              selectedEntity={selectedEntity}
              setSelectedEntity={setSelectedEntity}
              aiStudioEnabled={aiStudioEnabled}
              createType={createType}
              generatedContent={generatedContent}
              setGeneratedContent={setGeneratedContent}
              userTimezone={userTimezone}
              clonedJob={clonedJob}
            />
          )}
          {currentStep === 3 && (
            <NewJobStep3
              newJobData={newJobData}
              showEntitySection={showEntitySection}
              clonedJob={clonedJob}
              selectedEntity={selectedEntity}
              aiStudioEnabled={aiStudioEnabled}
              createType={createType}
              generatedContent={generatedContent}
              userTimezone={userTimezone}
              tlpPreviewCollapsed={tlpPreviewCollapsed}
              setTlpPreviewCollapsed={setTlpPreviewCollapsed}
              resources={resources}
              copiedJob={copiedJob}
            />
          )}
          <Box sx={classes.modalFooter}>
            {currentStep === 3 ? (
              <Button
                id="edit-new-job-button"
                type="submit"
                sx={classes.modalEditButton}
                onClick={() => setCurrentStep(currentStep - 1)}
              >
                Edit
              </Button>
            ) : (
              <span />
            )}
            <Button
              id={currentStep === 3 ? 'create-new-job-button' : 'next-new-job-button'}
              data-testid={currentStep === 3 ? 'create-new-job-button' : 'next-new-job-button'}
              type="submit"
              sx={classes.modalConfirmationButton}
              onClick={() => {
                if (isLoading) return;
                if (currentStep === 1) {
                  if (createType === 'Clone existing job') {
                    if (!clonedJob) {
                      setError(true);
                      fieldRefs['cloneJob']?.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                      });
                    } else {
                      setNewJobsData((prev) => ({
                        ...prev,
                        location: { ...prev.location, value: copiedJob?.location },
                        title: { ...prev.title, value: copiedJob?.title },
                        expires_at: { ...prev.expires_at, value: '' }
                      }));
                      const jobEntity = entities.find((e) => e.id === copiedJob?.entity_id);
                      jobEntity ? setSelectedEntity(jobEntity) : setSelectedEntity(entities[0]);
                      setCurrentStep(currentStep + 1);
                    }
                  } else setCurrentStep(currentStep + 1);
                }
                currentStep === 2 && handleValidateFields();
                currentStep === 3 && createType === 'Clone existing job' && cloneJob();
                currentStep === 3 && createType === 'Create new' && createJob();
              }}
            >
              {currentStep === 3 || currentStep === 1 ? (
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Confirm'
                )
              ) : (
                'Next'
              )}
            </Button>
          </Box>
          <GenericDialog
            isDialogOpen={confirmCancelIsOpen}
            setDialogOpen={setConfirmCancelIsOpen}
            title="Close without submitting?"
            description="Are you sure you want to cancel your job? You will lose any progress you have made."
            buttonCallback={() => {
              setConfirmCancelIsOpen(false);
              clearData();
            }}
            callbackLoading={false}
            buttonText="Close without saving"
            url=""
          />
        </Box>
        <ConfirmationDialog
          isDialogOpen={confirmCreatedJobOpen}
          setDialogOpen={setConfirmCreatedJobOpen}
          title="Complete"
          description="Your job has now been created."
          moreContent="To add more information or edit click below."
          buttonCallback={() => {
            setConfirmCreatedJobOpen(false);
            setFilters && setFilters({ ...filters });
            clearData();
          }}
          callbackLoading={false}
          buttonText="Continue editing"
          url={editJobUrl ? editJobUrl : ''}
        />
        <ConfirmationDialog
          isDialogOpen={confirmFailJobOpen}
          setDialogOpen={setConfirmFailJobOpen}
          title="Failed"
          description="Your job failed to be created."
          moreContent=""
          buttonCallback={() => {
            setConfirmFailJobOpen(false);
            clearData();
          }}
          callbackLoading={false}
          buttonText="Confirm"
          url={''}
        />
      </Box>
    </Modal>
  );
}
